import { useEffect, useState } from "react";
import { Search, Download, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { money } from "@/components/admin/AdminLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

const CATS = ["all", "electronics", "fashion", "home", "beauty", "fitness"];

export default function Import() {
  const [suppliers, setSuppliers] = useState([]);
  const [filters, setFilters] = useState({ supplier: "cjdropshipping", keywords: "", category: "all", min_price: "", max_price: "", warehouse: "all", min_stock: "" });
  const [results, setResults] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);

  useEffect(() => { api.get("/admin/import/meta").then((r) => setSuppliers(r.data.suppliers)); }, []);

  const search = async () => {
    setLoading(true); setSelected({});
    try {
      const body = { ...filters };
      body.min_price = filters.min_price ? parseFloat(filters.min_price) : null;
      body.max_price = filters.max_price ? parseFloat(filters.max_price) : null;
      body.min_stock = filters.min_stock ? parseInt(filters.min_stock) : null;
      const { data } = await api.post("/admin/import/search", body);
      setResults(data.results); setWarehouses(data.warehouses);
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const toggleAll = () => {
    const importable = results.filter((r) => !r.already_imported);
    const allSel = importable.every((r) => selected[r.supplier_product_id]);
    const next = {};
    importable.forEach((r) => { next[r.supplier_product_id] = !allSel; });
    setSelected(next);
  };

  const importItems = async (items) => {
    setImporting(true);
    try {
      const payload = items.map((r) => ({
        supplier: filters.supplier, supplier_product_id: r.supplier_product_id, title: r.title,
        description: r.description, category: r.category, images: r.images,
        supplier_price: r.supplier_price, shipping_cost: r.shipping_cost, warehouse: r.warehouse,
        stock: r.stock, delivery_days: r.delivery_days,
        variants: r.variants, selling_price: r.suggested_price,
        seo_title: r.title, seo_description: r.description?.slice(0, 160), tags: [r.category],
      }));
      const { data } = await api.post("/admin/import", { items: payload });
      toast.success(`Imported ${data.imported} product(s)`);
      search();
    } catch (e) { toast.error(apiErr(e)); } finally { setImporting(false); }
  };

  const selectedItems = results.filter((r) => selected[r.supplier_product_id]);

  return (
    <div className="space-y-5">
      <div><h1 className="font-display text-2xl font-bold tracking-tight">Import Products</h1><p className="text-sm text-zinc-500">Search connected suppliers and import into your store</p></div>

      <div className="bg-white border border-zinc-200 rounded-xl p-5 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div><Label>Supplier</Label>
          <Select value={filters.supplier} onValueChange={(v) => setFilters({ ...filters, supplier: v })}>
            <SelectTrigger data-testid="import-supplier"><SelectValue /></SelectTrigger>
            <SelectContent>{suppliers.map((s) => <SelectItem key={s.key} value={s.key}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Category</Label>
          <Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v })}>
            <SelectTrigger data-testid="import-category"><SelectValue /></SelectTrigger>
            <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="col-span-2"><Label>Keywords</Label><Input value={filters.keywords} onChange={(e) => setFilters({ ...filters, keywords: e.target.value })} placeholder="e.g. charger, hoodie" data-testid="import-keywords" /></div>
        <div><Label>Min Price</Label><Input type="number" value={filters.min_price} onChange={(e) => setFilters({ ...filters, min_price: e.target.value })} /></div>
        <div><Label>Max Price</Label><Input type="number" value={filters.max_price} onChange={(e) => setFilters({ ...filters, max_price: e.target.value })} /></div>
        <div><Label>Min Stock</Label><Input type="number" value={filters.min_stock} onChange={(e) => setFilters({ ...filters, min_stock: e.target.value })} /></div>
        <div className="flex items-end"><Button onClick={search} disabled={loading} className="w-full" data-testid="import-search-btn"><Search size={15} className="mr-2" />{loading ? "Searching..." : "Search"}</Button></div>
      </div>

      {results.length > 0 && (
        <div className="flex items-center justify-between bg-white border border-zinc-200 rounded-xl p-3 px-4">
          <div className="flex items-center gap-3">
            <Checkbox checked={results.filter((r) => !r.already_imported).length > 0 && results.filter((r) => !r.already_imported).every((r) => selected[r.supplier_product_id])} onCheckedChange={toggleAll} data-testid="select-all" />
            <span className="text-sm text-zinc-600">{selectedItems.length} selected of {results.length} results</span>
          </div>
          <Button onClick={() => importItems(selectedItems)} disabled={importing || selectedItems.length === 0} data-testid="import-selected-btn">
            {importing ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Download size={15} className="mr-2" />}Import Selected ({selectedItems.length})
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {results.map((r) => (
          <div key={r.supplier_product_id} className="bg-white border border-zinc-200 rounded-xl p-4 flex gap-4" data-testid={`import-result-${r.supplier_product_id}`}>
            <div className="relative shrink-0">
              <img src={(r.images || [])[0]} alt="" className="w-24 h-28 object-cover rounded-lg" />
              {!r.already_imported && <div className="absolute top-2 left-2"><Checkbox checked={!!selected[r.supplier_product_id]} onCheckedChange={() => toggle(r.supplier_product_id)} className="bg-white" data-testid={`select-${r.supplier_product_id}`} /></div>}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm line-clamp-1">{r.title}</p>
              <p className="text-xs text-zinc-400 capitalize mt-0.5">{r.category} · {r.warehouse}</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2 text-xs">
                <span className="text-zinc-500">Supplier: <span className="font-mono text-zinc-900">{money(r.supplier_price)}</span></span>
                <span className="text-zinc-500">Shipping: <span className="font-mono text-zinc-900">{money(r.shipping_cost)}</span></span>
                <span className="text-zinc-500">Stock: <span className="text-zinc-900">{r.stock}</span></span>
                <span className="text-zinc-500">Delivery: <span className="text-zinc-900">{r.delivery_days}d</span></span>
                <span className="text-zinc-500">Variants: <span className="text-zinc-900">{r.variants?.length || 0}</span></span>
                <span className="text-green-600 font-medium">Sell: {money(r.suggested_price)}</span>
              </div>
              <div className="mt-3">
                {r.already_imported ? (
                  <span className="text-xs text-green-600 flex items-center gap-1"><Check size={13} /> Already imported</span>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => importItems([r])} disabled={importing} data-testid={`import-one-${r.supplier_product_id}`}>
                    <Download size={13} className="mr-1.5" />Import
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {!loading && results.length === 0 && (
        <div className="text-center py-20 text-zinc-400"><Search size={40} className="mx-auto mb-3" /><p className="text-sm">Search a supplier catalog to begin importing products.</p></div>
      )}
    </div>
  );
}
